/**
 * TemplatePreview - Modal for previewing template content
 *
 * Features:
 * - Full content display
 * - Category-specific rendering
 * - Metadata display
 */
import { Mail, MessageSquare, FileText, ClipboardList, Copy, Check } from 'lucide-react';
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Button,
} from '@respectly/ui';
import type { GlobalTemplate } from '@respectly/shared-types';
import { TemplateCategoryBadge } from './TemplateCategoryBadge';
import { TemplateVisibilityBadge } from './TemplateVisibilityBadge';

// ============================================
// Types
// ============================================

interface TemplatePreviewProps {
  template: GlobalTemplate | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// ============================================
// Component
// ============================================

export function TemplatePreview({
  template,
  open,
  onOpenChange,
}: TemplatePreviewProps) {
  const [copied, setCopied] = useState(false);

  if (!template) return null;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(template.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Get icon based on category
  const getCategoryIcon = () => {
    switch (template.category) {
      case 'email':
        return Mail;
      case 'sms':
        return MessageSquare;
      case 'document':
        return FileText;
      case 'form':
        return ClipboardList;
      default:
        return FileText;
    }
  };

  const CategoryIcon = getCategoryIcon();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                <CategoryIcon className="h-5 w-5 text-slate-600 dark:text-slate-400" />
              </div>
              <div>
                <DialogTitle className="text-lg font-semibold">
                  {template.name}
                </DialogTitle>
                <div className="flex items-center gap-2 mt-1">
                  <TemplateCategoryBadge category={template.type} size="sm" />
                  <TemplateVisibilityBadge visibility={template.visibility} size="sm" />
                </div>
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* Content Area */}
        <div className="flex-1 min-h-0 mt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Content
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCopy}
              className="h-8"
            >
              {copied ? (
                <>
                  <Check className="mr-1.5 h-3.5 w-3.5 text-emerald-600" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="mr-1.5 h-3.5 w-3.5" />
                  Copy
                </>
              )}
            </Button>
          </div>
          <div className="h-[300px] overflow-auto rounded-lg border bg-slate-50 dark:bg-slate-900 dark:border-slate-700">
            <pre className="p-4 text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap font-mono">
              {template.content}
            </pre>
          </div>
        </div>

        {/* Metadata */}
        <div className="flex-shrink-0 mt-4 pt-4 border-t dark:border-slate-700">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-slate-500 dark:text-slate-400">Usage</span>
              <p className="font-medium text-slate-900 dark:text-white">
                {template.usageCount} tenants using this template
              </p>
            </div>
            <div>
              <span className="text-slate-500 dark:text-slate-400">Status</span>
              <p className="font-medium text-slate-900 dark:text-white">
                {template.isActive ? 'Active' : 'Inactive'}
              </p>
            </div>
            <div>
              <span className="text-slate-500 dark:text-slate-400">Created</span>
              <p className="font-medium text-slate-900 dark:text-white">
                {new Date(template.createdAt).toLocaleDateString()}
              </p>
            </div>
            <div>
              <span className="text-slate-500 dark:text-slate-400">Last Updated</span>
              <p className="font-medium text-slate-900 dark:text-white">
                {new Date(template.updatedAt).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
